import React, { useEffect, useRef, useState } from "react";
import {
  FaArrowLeft,
  FaCheck,
  FaMinusCircle,
  FaExclamationTriangle,
} from "react-icons/fa";
import { useNavigate, useSearchParams } from "react-router-dom";
import Api from "../../../config";
import {
  ADD_BUTTON,
  BLUE_ADD,
  GO_BACK,
  PDF,
  SUCCESS_IMAGE,
  UPLOAD_ICON,
} from "../../../assets";
import toast from "react-hot-toast";
import { useAppSelector } from "../../../hooks";
import {
  getSelectedCase,
  party_type,
  suit_number,
} from "../../../states/slices/partyInfo.slice";
import { getFormDataHeader } from "../../../utils";
import NIGERIA_STATES from "../../../assets/data/states.json";
import STATE_LGAS from "../../../assets/data/lgas.json";
import Modal from "../../../components/UI/modal";
import Preloader from "../../../components/preloader";
import {
  validateCreateCaseForm,
  validateFile,
  validateParty,
  validateCounsel,
  validateCaseDocuments,
  sanitizeInput,
  type PartyData,
  type CounselData,
  type CaseDocument,
} from "../../../utils/validation";
import { documentType } from "../../../data/documentType";
import { formsToUpload } from "../../../data/forms";

const StepSignal = ({
  step,
  currentStep,
  title,
  onClick,
}: {
  step: number;
  currentStep: number;
  title: string;
  onClick?: () => void;
}) => (
  <div
    className="flex flex-col items-center gap-y-1 cursor-pointer"
    onClick={onClick}
  >
    <div
      className={`w-[100px] rounded-3xl h-[12px] ${
        currentStep >= step ? "  bg-[#BDBEE0]" : " bg-[#E5E5E5]"
      } `}
    ></div>
    <span
      className={` ${
        currentStep >= step ? "text-black" : "text-[#9A9A9A]"
      } text-xs font-normal `}
    >
      {title}{" "}
    </span>
  </div>
);

function CreateCase() {
  const [currentStep, setCurrentStep] = useState(1);
  const [caseCategory, setCaseCategory] = useState("");
  const [caseCategories, setCaseCategories] = useState<
    { id: string; title: string }[]
  >([]);

  const [showDocumentTypeModal, setShowDocumentTypeModal] = useState(false);
  const [plaintiffField, setPlaintiffField] = useState<PartyData[]>([
    {
      partyType: "",
      first_name: "",
      last_name: "",
      other_name: "",
      email: "",
      address: "",
      phone_number: "",
      state: "",
      lga: "",
      children_names: [],
      next_of_kin: [],
    },
  ]);
  const [defendantField, setDefendantField] = useState<PartyData[]>([
    {
      partyType: "",
      first_name: "",
      last_name: "",
      other_name: "",
      email: "",
      address: "",
      phone_number: "",
      state: "",
      lga: "",
      children_names: [],
      next_of_kin: [],
    },
  ]);

  const [caseDocument, setCaseDocument] = useState<CaseDocument[]>([
    {
      title: "",
      document_type: "",
      file: null,
    },
  ]);

  // Enhanced validation states
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [counselValid, setCounselValid] = useState<boolean>(false);
  const [stepValidation, setStepValidation] = useState<Record<number, boolean>>(
    {
      1: false,
      2: false,
      3: false,
      4: false,
    },
  );
  const [startedTyping, setStartedTyping] = useState<boolean>(false);

  const navigate = useNavigate();
  const fileInputRef = useRef<any>([]);
  const getSuitNumber = useAppSelector(suit_number);
  const selectedPartyType = useAppSelector(party_type);
  const [formdata, setFormData] = useState<CounselData>({
    counsel_detail_first_name: "",
    counsel_detail_whatsapp_number: "",
    counsel_detail_phone_number: "",
    counsel_detail_email: "",
    counsel_detail_address: [""],
    counsel_detail_firm_name: "",
    counsel_detail_last_name: "",
  });
  const [successModal, setSuccessModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const filterEdDocumentsToUpload = formsToUpload.filter((item) =>
    item.type.includes(caseCategory),
  );
  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    id: any,
  ) => {
    const file = e.target.files && e.target.files[0];

    if (file) {
      // Validate file before setting
      const fileError = validateFile(file);
      if (fileError) {
        toast.error(fileError);
        return;
      }
    }

    const updatedCaseDocs = caseDocument.map((doc, i) =>
      i === id ? { ...doc, file } : doc,
    );

    setCaseDocument(updatedCaseDocs);
    setStartedTyping(true);
    validateStep(3);
  };

  const handleCaseInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    index: number,
  ) => {
    const { name, value } = e.target;
    const sanitizedValue = sanitizeInput(value);

    const updatedCaseDoc = caseDocument.map((doc, i) =>
      i === index ? { ...doc, [name]: sanitizedValue } : doc,
    );

    setCaseDocument(updatedCaseDoc);
    setStartedTyping(true);
    validateStep(3);
  };

  const handleDefendantInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    index: number,
  ) => {
    const { name, value } = e.target;
    const sanitizedValue = sanitizeInput(value);

    const updatedDefendants = defendantField.map((defendant, i) =>
      i === index ? { ...defendant, [name]: sanitizedValue } : defendant,
    );

    setDefendantField(updatedDefendants);
    setStartedTyping(true);
    validateStep(1);
  };

  const handleClaimantInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    index: number,
  ) => {
    const { name, value } = e.target;
    const sanitizedValue = sanitizeInput(value);

    const updatedPlainTiffs = plaintiffField.map((plaintiff, i) =>
      i === index ? { ...plaintiff, [name]: sanitizedValue } : plaintiff,
    );

    setPlaintiffField(updatedPlainTiffs);
    setStartedTyping(true);
    validateStep(1);
  };

  const handleUpdatePlainTiffArray = (
    index: number,
    field: "children_names" | "next_of_kin",
    value: any,
  ) => {
    const updatedPlainTiffs = plaintiffField.map((plaintiff, i) =>
      i === index ? { ...plaintiff, [field]: value } : plaintiff,
    );
    setPlaintiffField(updatedPlainTiffs);
    setStartedTyping(true);
    validateStep(1);
  };

  const handleUpdateDefendantArray = (
    index: number,
    field: "children_names" | "next_of_kin",
    value: any,
  ) => {
    const updatedDefendants = defendantField.map((defendant, i) =>
      i === index ? { ...defendant, [field]: value } : defendant,
    );
    setDefendantField(updatedDefendants);
    setStartedTyping(true);
    validateStep(1);
  };

  const handleAddCaseField = () => {
    setCaseDocument([
      ...caseDocument,
      {
        title: "",
        document_type: "",
        file: null,
      },
    ]);
  };

  const handleDeletePlaintiff = (index: number) => {
    setPlaintiffField((prevPlaintiff) =>
      prevPlaintiff.filter((_, i) => i !== index),
    );
  };
  const handleDeleteDefendant = (index: number) => {
    setDefendantField((prevDefendant) =>
      prevDefendant.filter((_, i) => i !== index),
    );
  };
  // Enhanced validation functions
  const validateStep = (step: number) => {
    let isValid = false;

    switch (step) {
      case 1:
        isValid = validatePartiesStep();
        break;
      case 2:
        isValid = validateCounselStep();
        break;
      case 3:
        isValid = validateDocumentsStep();
        break;
      case 4:
        isValid = true;
        break;
      default:
        isValid = false;
    }

    setStepValidation((prev) => ({ ...prev, [step]: isValid }));
    return isValid;
  };

  const validatePartiesStep = (): boolean => {
    const errors: string[] = [];
    const newFieldErrors: Record<string, string> = {};

    if (!caseCategory) {
      newFieldErrors["caseCategory"] = "Case category is required";
      errors.push("Case category is required");
    }

    if (plaintiffField.length === 0) {
      errors.push("At least one plaintiff is required");
    } else {
      plaintiffField.forEach((plaintiff, index) => {
        const validation = validateParty(
          plaintiff,
          index,
          "plaintiff",
          caseCategory,
        );
        if (!validation.isValid) {
          errors.push(...validation.errors);
        }
        Object.keys(validation.fieldErrors).forEach((field) => {
          newFieldErrors[`plaintiff_${index}_${field}`] =
            validation.fieldErrors[field];
        });
      });
    }

    if (defendantField.length === 0) {
      errors.push("At least one defendant is required");
    } else {
      defendantField.forEach((defendant, index) => {
        const validation = validateParty(
          defendant,
          index,
          "defendant",
          caseCategory,
        );
        if (!validation.isValid) {
          errors.push(...validation.errors);
        }
        Object.keys(validation.fieldErrors).forEach((field) => {
          newFieldErrors[`defendant_${index}_${field}`] =
            validation.fieldErrors[field];
        });
      });
    }

    setFieldErrors((prev) => {
      const rest = Object.keys(prev)
        .filter(
          (key) =>
            !key.startsWith("plaintiff_") &&
            !key.startsWith("defendant_") &&
            key !== "caseCategory",
        )
        .reduce((acc, key) => ({ ...acc, [key]: prev[key] }), {});
      return { ...rest, ...newFieldErrors };
    });
    return errors.length === 0;
  };

  const validateCounselStep = (): boolean => {
    const validation = validateCounsel(formdata);

    setFieldErrors((prev) => {
      const rest = Object.keys(prev)
        .filter((key) => !key.startsWith("counsel_detail_"))
        .reduce((acc, key) => ({ ...acc, [key]: prev[key] }), {});
      return { ...rest, ...validation.fieldErrors };
    });

    return validation.isValid;
  };

  const validateDocumentsStep = (): boolean => {
    const validation = validateCaseDocuments(caseDocument);

    setFieldErrors((prev) => {
      const rest = Object.keys(prev)
        .filter((key) => !key.startsWith("document_"))
        .reduce((acc, key) => ({ ...acc, [key]: prev[key] }), {});
      return { ...rest, ...validation.fieldErrors };
    });

    return validation.isValid;
  };

  const validateEntireForm = (): boolean => {
    const validation = validateCreateCaseForm(
      plaintiffField,
      defendantField,
      formdata,
      caseDocument,
      caseCategory,
    );

    // Add case category validation check
    const isValid = validation.isValid && !!caseCategory;

    setValidationErrors(
      validation.errors.reduce(
        (acc, error, index) => ({ ...acc, [index]: error }),
        {},
      ),
    );
    setFieldErrors(validation.fieldErrors);
    setIsFormValid(isValid);
    return isValid;
  };

  const handleAddDefendantField = () => {
    setDefendantField([
      ...defendantField,
      {
        first_name: "",
        partyType: "",
        email: "",
        address: "",
        phone_number: "",
        last_name: "",
        other_name: "",
        state: "",
        lga: "",
        children_names: [],
        next_of_kin: [],
      },
    ]);
  };
  const handleAddPlaintiffField = () => {
    setPlaintiffField([
      ...plaintiffField,
      {
        partyType: "",
        first_name: "",
        last_name: "",
        other_name: "",
        email: "",
        address: "",
        phone_number: "",
        state: "",
        lga: "",
        children_names: [],
        next_of_kin: [],
      },
    ]);
  };

  const handleNext = () => {
    // Validate current step before proceeding
    const isCurrentStepValid = validateStep(currentStep);

    if (!isCurrentStepValid) {
      setStartedTyping(true);
      toast.error(
        "Please fill in all required fields correctly before proceeding",
      );
      return;
    }

    setCurrentStep((prev) => prev + 1);
  };

  const handlePrev = () => {
    setCurrentStep((prev) => prev - 1);
  };
  const handleRemoveFileCaseField = (index: number) => {
    const updatedCaseDoc = caseDocument.map((doc, i) =>
      i === index ? { ...doc, file: null } : doc,
    );

    setCaseDocument(updatedCaseDoc);
    // Clear the specific file input value
    if (fileInputRef.current[index]) {
      fileInputRef.current[index].value = "";
    }
  };

  const handleViewDocument = (file: File | null) => {
    if (file) {
      const fileUrl = URL.createObjectURL(file);
      window.open(fileUrl, "_blank");
    }
  };
  const handleRemoveCaseField = (index: number) => {
    const updatedCaseField = caseDocument.filter((_, i) => i !== index);
    setCaseDocument(updatedCaseField);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const sanitizedValue = sanitizeInput(value);

    setFormData((prevData) => ({
      ...prevData,
      [name]: sanitizedValue,
    }));
    setStartedTyping(true);
    validateStep(2);
  };

  const getCase = useAppSelector(getSelectedCase);

  // Enhanced error display component
  const ErrorMessage = ({ message }: { message: string }) => {
    if (!startedTyping) return null;
    return (
      <div className="flex items-center gap-2 text-red-500 text-sm mt-1">
        <FaExclamationTriangle size={12} />
        <span>{message}</span>
      </div>
    );
  };

  // Get field error helper
  const getFieldError = (fieldKey: string): string | null => {
    return fieldErrors[fieldKey] || null;
  };

  const handleGetCaseCategories = async () => {
    const response = await Api.get("/case-categories", getFormDataHeader());
    if (response) {
      setCaseCategories(response?.data?.data);
    }
  };

  useEffect(() => {
    handleGetCaseCategories();
  }, []);

  const [preloaderState, setPreloaderState] = useState<
    "creating" | "almostDone" | "success" | "error" | null
  >(null);
  const handleCreateCase = async () => {
    const selectedCategory = caseCategories?.find(
      (cat) => cat.title === caseCategory,
    );
    const caseCategoryId = selectedCategory?.id;

    const parties = [
      ...plaintiffField.map((plaintiff) => ({
        first_name: plaintiff.first_name,
        last_name: plaintiff.last_name,
        other_name: plaintiff.other_name,
        phone_number: plaintiff.phone_number,
        email: plaintiff.email,
        title: plaintiff.partyType,
        address: plaintiff.address,
        state: plaintiff.state,
        lga: plaintiff.lga,
        children_names: plaintiff.children_names,
        next_of_kin: plaintiff.next_of_kin,
      })),
      ...defendantField.map((defendant) => ({
        first_name: defendant.first_name,
        last_name: defendant.last_name,
        other_name: defendant.other_name,
        phone_number: defendant.phone_number,
        email: defendant.email,
        title: defendant.partyType,
        address: defendant.address,
        state: defendant.state,
        lga: defendant.lga,
        children_names: defendant.children_names,
        next_of_kin: defendant.next_of_kin,
      })),
    ];

    const payload = {
      // case_category: caseCategoryId,
      parties,
      documents: caseDocument.map((doc) => ({
        title: doc.title,
        type: doc.document_type.toUpperCase(),
        case_category_id: caseCategoryId,
        file_path: doc.file,
      })),

      counsel_detail_first_name: formdata.counsel_detail_first_name,
      counsel_detail_last_name: formdata.counsel_detail_last_name,
      counsel_detail_firm_name: formdata.counsel_detail_firm_name,
      counsel_detail_email: formdata.counsel_detail_email,
      counsel_detail_phone_number: formdata.counsel_detail_phone_number,
      counsel_detail_whatsapp_phone_number:
        formdata.counsel_detail_whatsapp_number,
      counsel_detail_addresses: formdata.counsel_detail_address,
    };

    try {
      setPreloaderState("creating");

      const response = await Api.post(
        "/lawyer/case-folders",
        payload,
        getFormDataHeader(),
      );

      if (response) {
        setTimeout(() => setPreloaderState("almostDone"), 1000);

        setTimeout(() => {
          setPreloaderState("success");
          setSuccessModal(true);
          toast.success(response?.data?.message);
          // setTimeout(() => {
          //   navigate("/lawyer/my-files");
          // }, 1000);
        }, 2000);
      }
    } catch (error: any) {
      setPreloaderState("error");
      toast.error(error?.response?.data?.message);
    } finally {
      setTimeout(() => setPreloaderState(null), 3000);
    }
  };

  useEffect(() => {
    return () => {
      fileInputRef.current = [];
    };
  }, []);

  // Validate current step when data changes
  useEffect(() => {
    validateStep(currentStep);
  }, [
    plaintiffField,
    defendantField,
    formdata,
    caseDocument,
    currentStep,
    caseCategory,
  ]);

  // Reset startedTyping when moving to next step
  useEffect(() => {
    setStartedTyping(false);
  }, [currentStep]);

  // console.log(caseCategories);

  return (
    <main className="py-7 px-7">
      <img
        src={GO_BACK}
        className="w-[32px] h-[32px] cursor-pointer"
        alt=""
        onClick={() => window.history.back()}
      />

      <div className="flex items-center justify-center flex-col mt-20">
        <span className="text-[28px] font-semibold text-black">
          Create new case folder
        </span>
        <div className="flex items-center gap-x-5 mt-5">
          <StepSignal
            step={1}
            currentStep={currentStep}
            title="Details of party"
          />
          <StepSignal
            step={2}
            currentStep={currentStep}
            title="Details of counsel"
          />
          <StepSignal
            step={3}
            currentStep={currentStep}
            title="Upload documents"
          />
          <StepSignal step={4} currentStep={currentStep} title="Preview " />
        </div>

        {currentStep === 1 && (
          <div>
            <div className="w-[575px] flex flex-col gap-y-5 h-full shadow-lg bg-white px-16 py-10 mt-6">
              <span className="text-[20px] font-semibold text-black">
                Case categories
              </span>

              <div className="flex flex-col items-start">
                <select
                  name="caseCategory"
                  value={caseCategory}
                  onChange={(e) => {
                    setCaseCategory(e.target.value);
                    setStartedTyping(true);
                  }}
                  className={`w-full rounded border outline-none bg-transparent placeholder:text-[16px] text-[16px] placeholder:text-black placeholder:font-semibold font-semibold py-4 px-3 ${
                    fieldErrors["caseCategory"]
                      ? "border-red-500"
                      : "border-[#CACACA]"
                  }`}
                >
                  <option value="">Select category</option>
                  {caseCategories?.map((category) => (
                    <option key={category?.id} value={category?.title}>
                      {category?.title}
                    </option>
                  ))}
                </select>
                {fieldErrors["caseCategory"] && (
                  <ErrorMessage message={fieldErrors["caseCategory"]} />
                )}
              </div>
            </div>

            <div className="w-[575px] flex flex-col gap-y-5 h-full shadow-lg bg-white px-16 py-10 mt-6">
              <span className="text-[20px] font-semibold text-black">
                Description of parties
              </span>

              {plaintiffField.map((plaintiff, i) => (
                <div className="flex flex-col items-start" key={i}>
                  <select
                    name="partyType"
                    value={plaintiff.partyType}
                    onChange={(e) => handleClaimantInputChange(e, i)}
                    className={`w-full rounded border-b outline-none bg-transparent placeholder:text-[16px] text-[16px] placeholder:text-black placeholder:font-semibold font-semibold py-4 px-3 ${
                      getFieldError(`plaintiff_${i}_partyType`)
                        ? "border-red-500"
                        : "border-[#CACACA]"
                    }`}
                  >
                    <option value="">Select party</option>
                    <option value="PLAINTIFF">Plaintiff</option>
                    <option value="PETITIONER">Petitioner</option>
                    <option value="APPLICANT">Applicant</option>
                    <option value="CLAIMANT">Claimant</option>
                    <option value="PROBATION OFFICER">
                      The Probation Officer
                    </option>
                    <option value="CHILD DEVELOPMENT OFFICER">
                      The Child development officer
                    </option>
                    <option value="STATE">The State</option>
                    <option value="COMMISSIONER OF POLICE">
                      Commissioner of Police
                    </option>
                  </select>
                  {getFieldError(`plaintiff_${i}_partyType`) && (
                    <ErrorMessage
                      message={getFieldError(`plaintiff_${i}_partyType`)!}
                    />
                  )}
                  <div className="flex mt-4 w-full flex-col gap-y-5 mb-5">
                    <div>
                      <input
                        type="text"
                        name="first_name"
                        className={`w-full border rounded outline-none bg-transparent placeholder:text-sm placeholder:text-black py-4 px-3 ${
                          getFieldError(`plaintiff_${i}_first_name`)
                            ? "border-red-500"
                            : "border-[#CACACA]"
                        }`}
                        placeholder="First name"
                        onChange={(e) => handleClaimantInputChange(e, i)}
                        value={plaintiff.first_name}
                      />
                      {getFieldError(`plaintiff_${i}_first_name`) && (
                        <ErrorMessage
                          message={getFieldError(`plaintiff_${i}_first_name`)!}
                        />
                      )}
                    </div>
                    <div>
                      <input
                        type="text"
                        name="last_name"
                        className={`w-full border rounded outline-none bg-transparent placeholder:text-sm placeholder:text-black py-4 px-3 ${
                          getFieldError(`plaintiff_${i}_last_name`)
                            ? "border-red-500"
                            : "border-[#CACACA]"
                        }`}
                        placeholder="Last name"
                        onChange={(e) => handleClaimantInputChange(e, i)}
                        value={plaintiff.last_name}
                      />
                      {/* {getFieldError(`plaintiff_${i}_last_name`) && (
                        <ErrorMessage
                          message={getFieldError(`plaintiff_${i}_last_name`)!}
                        />
                      )} */}
                    </div>
                    <div>
                      <input
                        type="text"
                        name="other_name"
                        className={`w-full border rounded outline-none bg-transparent placeholder:text-sm placeholder:text-black py-4 px-3 ${
                          getFieldError(`plaintiff_${i}_other_name`)
                            ? "border-red-500"
                            : "border-[#CACACA]"
                        }`}
                        placeholder="Other name (optional)"
                        onChange={(e) => handleClaimantInputChange(e, i)}
                        value={plaintiff.other_name}
                      />
                      {/* {getFieldError(`plaintiff_${i}_other_name`) && (
                        <ErrorMessage
                          message={getFieldError(`plaintiff_${i}_other_name`)!}
                        />
                      )} */}
                    </div>
                  </div>
                  <div className="mb-5 w-full">
                    <input
                      type="email"
                      name="email"
                      className={`w-full border rounded outline-none bg-transparent placeholder:text-sm placeholder:text-black py-4 px-3 ${
                        getFieldError(`plaintiff_${i}_email`)
                          ? "border-red-500"
                          : "border-[#CACACA]"
                      }`}
                      placeholder="Email address (optional)"
                      onChange={(e) => handleClaimantInputChange(e, i)}
                      value={plaintiff.email}
                    />
                    {/* {getFieldError(`plaintiff_${i}_email`) && (
                      <ErrorMessage
                        message={getFieldError(`plaintiff_${i}_email`)!}
                      />
                    )} */}
                  </div>
                  <div className="flex w-full flex-col mb-5">
                    <input
                      type="text"
                      name="address"
                      className={`w-full border rounded outline-none bg-transparent placeholder:text-sm placeholder:text-black py-4 px-3 ${
                        getFieldError(`plaintiff_${i}_address`)
                          ? "border-red-500"
                          : "border-[#CACACA]"
                      }`}
                      placeholder="Address"
                      onChange={(e) => handleClaimantInputChange(e, i)}
                      value={plaintiff.address}
                    />
                    {/* {getFieldError(`plaintiff_${i}_address`) && (
                      <ErrorMessage
                        message={getFieldError(`plaintiff_${i}_address`)!}
                      />
                    )} */}
                  </div>
                  <div className="mb-5 w-full">
                    <input
                      type="tel"
                      name="phone_number"
                      className={`w-full border rounded outline-none bg-transparent placeholder:text-sm placeholder:text-black py-4 px-3 ${
                        getFieldError(`plaintiff_${i}_phone_number`)
                          ? "border-red-500"
                          : "border-[#CACACA]"
                      }`}
                      placeholder="Phone number (e.g., 08012345678)"
                      onChange={(e) => handleClaimantInputChange(e, i)}
                      value={plaintiff.phone_number}
                    />
                    {/* {getFieldError(`plaintiff_${i}_phone_number`) && (
                      <ErrorMessage
                        message={getFieldError(`plaintiff_${i}_phone_number`)!}
                      />
                    )} */}
                  </div>

                  <div className="flex w-full gap-x-5 mb-5">
                    <div className="w-full">
                      <select
                        name="state"
                        className={`w-full border rounded outline-none bg-transparent py-4 px-3 ${
                          getFieldError(`plaintiff_${i}_state`)
                            ? "border-red-500"
                            : "border-[#CACACA]"
                        }`}
                        onChange={(e) => handleClaimantInputChange(e, i)}
                        value={plaintiff.state}
                      >
                        <option value="">Select State</option>
                        {NIGERIA_STATES.map((state: string) => (
                          <option key={state} value={state}>
                            {state}
                          </option>
                        ))}
                      </select>
                      {/* {getFieldError(`plaintiff_${i}_state`) && (
                        <ErrorMessage
                          message={getFieldError(`plaintiff_${i}_state`)!}
                        />
                      )} */}
                    </div>
                    <div className="w-full">
                      <select
                        name="lga"
                        className={`w-full border rounded outline-none bg-transparent py-4 px-3 ${
                          getFieldError(`plaintiff_${i}_lga`)
                            ? "border-red-500"
                            : "border-[#CACACA]"
                        }`}
                        onChange={(e) => handleClaimantInputChange(e, i)}
                        value={plaintiff.lga}
                        disabled={!plaintiff.state}
                      >
                        <option value="">Select LGA</option>
                        {plaintiff.state &&
                          (STATE_LGAS as Record<string, string[]>)[
                            plaintiff.state
                          ]?.map((lga) => (
                            <option key={lga} value={lga}>
                              {lga}
                            </option>
                          ))}
                      </select>
                      {/* {getFieldError(`plaintiff_${i}_lga`) && (
                        <ErrorMessage
                          message={getFieldError(`plaintiff_${i}_lga`)!}
                        />
                      )} */}
                    </div>
                  </div>

                  {/* Children Names Section */}

                  <div className="mb-5 w-full">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold">
                        Children Names{" "}
                        {/* <span className="text-red-500 font-bold">*</span> */}
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          handleUpdatePlainTiffArray(i, "children_names", [
                            ...plaintiff.children_names,
                            "",
                          ])
                        }
                        className="text-xs text-blue-600 flex items-center gap-1"
                      >
                        <img src={BLUE_ADD} className="w-4 h-4" alt="" />
                        Add Child
                      </button>
                    </div>
                    {plaintiff.children_names.map((child, childIdx) => (
                      <div key={childIdx} className="flex gap-2 mb-2">
                        <div className="flex-1 flex flex-col">
                          <input
                            type="text"
                            placeholder="Child name"
                            value={child}
                            onChange={(e) => {
                              const newChildren = [...plaintiff.children_names];
                              newChildren[childIdx] = e.target.value;
                              handleUpdatePlainTiffArray(
                                i,
                                "children_names",
                                newChildren,
                              );
                            }}
                            className={`w-full border rounded py-2 px-3 text-sm ${
                              getFieldError(`plaintiff_${i}_child_${childIdx}`)
                                ? "border-red-500"
                                : "border-[#CACACA]"
                            }`}
                          />
                          {/* {getFieldError(
                              `plaintiff_${i}_child_${childIdx}`,
                            ) && (
                              <ErrorMessage
                                message={
                                  getFieldError(
                                    `plaintiff_${i}_child_${childIdx}`,
                                  )!
                                }
                              />
                            
                            )} */}
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const newChildren = plaintiff.children_names.filter(
                              (_, idx) => idx !== childIdx,
                            );
                            handleUpdatePlainTiffArray(
                              i,
                              "children_names",
                              newChildren,
                            );
                          }}
                          className="text-red-500"
                        >
                          <FaMinusCircle size={18} />
                        </button>
                      </div>
                    ))}
                    {/* {getFieldError(`plaintiff_${i}_children_names`) && (
                        <ErrorMessage
                          message={
                            getFieldError(`plaintiff_${i}_children_names`)!
                          }
                        />
                      )} */}
                  </div>

                  {/* Next of Kin Section */}
                  <div className="mb-5 w-full">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold">Next of Kin</span>
                      <button
                        type="button"
                        onClick={() =>
                          handleUpdatePlainTiffArray(i, "next_of_kin", [
                            ...plaintiff.next_of_kin,
                            { name: "", email: "", phone_number: "" },
                          ])
                        }
                        className="text-xs text-blue-600 flex items-center gap-1"
                      >
                        <img src={BLUE_ADD} className="w-4 h-4" alt="" />
                        Add Next of Kin
                      </button>
                    </div>
                    {plaintiff.next_of_kin.map((nok, nokIdx) => (
                      <div
                        key={nokIdx}
                        className="border p-3 rounded mb-2 border-[#CACACA] relative"
                      >
                        <button
                          type="button"
                          onClick={() => {
                            const newNok = plaintiff.next_of_kin.filter(
                              (_, idx) => idx !== nokIdx,
                            );
                            handleUpdatePlainTiffArray(
                              i,
                              "next_of_kin",
                              newNok,
                            );
                          }}
                          className="absolute top-2 right-2 text-red-500"
                        >
                          <FaMinusCircle size={18} />
                        </button>
                        <div className="flex flex-col gap-2">
                          <input
                            type="text"
                            placeholder="Name"
                            value={nok.name}
                            onChange={(e) => {
                              const newNok = [...plaintiff.next_of_kin];
                              newNok[nokIdx] = { ...nok, name: e.target.value };
                              handleUpdatePlainTiffArray(
                                i,
                                "next_of_kin",
                                newNok,
                              );
                            }}
                            className="w-full border rounded py-2 px-3 text-sm border-[#CACACA]"
                          />
                          <input
                            type="email"
                            placeholder="Email (optional)"
                            value={nok.email}
                            onChange={(e) => {
                              const newNok = [...plaintiff.next_of_kin];
                              newNok[nokIdx] = {
                                ...nok,
                                email: e.target.value,
                              };
                              handleUpdatePlainTiffArray(
                                i,
                                "next_of_kin",
                                newNok,
                              );
                            }}
                            className="w-full border rounded py-2 px-3 text-sm border-[#CACACA]"
                          />
                          <input
                            type="tel"
                            placeholder="Phone Number"
                            value={nok.phone_number}
                            onChange={(e) => {
                              const newNok = [...plaintiff.next_of_kin];
                              newNok[nokIdx] = {
                                ...nok,
                                phone_number: e.target.value,
                              };
                              handleUpdatePlainTiffArray(
                                i,
                                "next_of_kin",
                                newNok,
                              );
                            }}
                            className="w-full border rounded py-2 px-3 text-sm border-[#CACACA]"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                  {/* Add/Delete buttons here */}
                  <div className="flex items-center justify-between w-full">
                    {plaintiffField.length > 1 && (
                      <button
                        className=" flex items-center gap-x-2 text-sm rounded py-3  text-[#272755] font-semibold"
                        onClick={() => handleDeletePlaintiff(i)}
                      >
                        <span className="font-medium text-sm text-red-500">
                          Delete Field
                        </span>
                        <FaMinusCircle size={20} color="red" />
                      </button>
                    )}
                    <button
                      className={`flex items-center ${
                        plaintiffField.length <= 1 &&
                        "w-full items-end justify-end"
                      } gap-x-2 text-sm rounded py-3  text-[#272755] font-semibold`}
                      onClick={handleAddPlaintiffField}
                    >
                      <span className="font-medium text-sm text-black">
                        Add Claimant
                      </span>
                      <img
                        src={BLUE_ADD}
                        className="w-[20px] h-[20px]"
                        alt=""
                      />
                    </button>
                  </div>
                </div>
              ))}
              {defendantField.map((defendant, i) => (
                <div className="flex flex-col items-start">
                  <select
                    name="partyType"
                    id=""
                    value={defendant.partyType}
                    onChange={(e) => handleDefendantInputChange(e, i)}
                    className={`w-full rounded border-b outline-none bg-transparent placeholder:text-[16px] text-[16px] placeholder:text-black placeholder:font-semibold font-semibold py-4 px-3 ${
                      getFieldError(`defendant_${i}_partyType`)
                        ? "border-red-500"
                        : "border-[#CACACA]"
                    }`}
                  >
                    <option value="">Select party</option>
                    <option value="DEFENDANT">Defendant</option>
                    <option value="RESPONDENT">Respondent</option>
                    <option value="PROBATION OFFICER">
                      The Probation Officer
                    </option>
                    <option value="CHILD DEVELOPMENT OFFICER">
                      The Child development officer
                    </option>
                    <option value="STATE">The State</option>
                    <option value="COMMISSIONER OF POLICE">
                      Commissioner of Police
                    </option>
                  </select>
                  {getFieldError(`defendant_${i}_partyType`) && (
                    <ErrorMessage
                      message={getFieldError(`defendant_${i}_partyType`)!}
                    />
                  )}
                  <div className="flex w-full mt-4 items-end justify-end flex-col gap-y-5 mb-5">
                    <div className="w-full">
                      <input
                        type="text"
                        name="first_name"
                        className={`w-full border rounded outline-none bg-transparent placeholder:text-sm placeholder:text-black py-4 px-3 ${
                          getFieldError(`defendant_${i}_first_name`)
                            ? "border-red-500"
                            : "border-[#CACACA]"
                        }`}
                        placeholder="First name"
                        onChange={(e) => handleDefendantInputChange(e, i)}
                        value={defendant.first_name}
                      />
                      {/* {getFieldError(`defendant_${i}_first_name`) && (
                        <ErrorMessage
                          message={getFieldError(`defendant_${i}_first_name`)!}
                        />
                      )} */}
                    </div>
                    <div className="w-full">
                      <input
                        type="text"
                        name="last_name"
                        className={`w-full border rounded outline-none bg-transparent placeholder:text-sm placeholder:text-black py-4 px-3 ${
                          getFieldError(`defendant_${i}_last_name`)
                            ? "border-red-500"
                            : "border-[#CACACA]"
                        }`}
                        placeholder="Last name"
                        onChange={(e) => handleDefendantInputChange(e, i)}
                        value={defendant.last_name}
                      />
                      {/* {getFieldError(`defendant_${i}_last_name`) && (
                        <ErrorMessage
                          message={getFieldError(`defendant_${i}_last_name`)!}
                        />
                      )} */}
                    </div>
                    <div className="w-full">
                      <input
                        type="text"
                        name="other_name"
                        className={`w-full border rounded outline-none bg-transparent placeholder:text-sm placeholder:text-black py-4 px-3 ${
                          getFieldError(`defendant_${i}_other_name`)
                            ? "border-red-500"
                            : "border-[#CACACA]"
                        }`}
                        placeholder="Other name (optional)"
                        onChange={(e) => handleDefendantInputChange(e, i)}
                        value={defendant.other_name}
                      />
                      {/* {getFieldError(`defendant_${i}_other_name`) && (
                        <ErrorMessage
                          message={getFieldError(`defendant_${i}_other_name`)!}
                        />
                      )} */}
                    </div>
                  </div>
                  <div className="mb-5 w-full">
                    <input
                      type="email"
                      name="email"
                      className={`w-full border rounded outline-none bg-transparent placeholder:text-sm placeholder:text-black py-4 px-3 ${
                        getFieldError(`defendant_${i}_email`)
                          ? "border-red-500"
                          : "border-[#CACACA]"
                      }`}
                      placeholder="Email address (optional)"
                      onChange={(e) => handleDefendantInputChange(e, i)}
                      value={defendant.email}
                    />
                    {/* {getFieldError(`defendant_${i}_email`) && (
                      <ErrorMessage
                        message={getFieldError(`defendant_${i}_email`)!}
                      />
                    )} */}
                  </div>
                  <div className="flex items-end justify-end flex-col mb-5 w-full">
                    <input
                      type="text"
                      name="address"
                      className={`w-full border rounded outline-none bg-transparent placeholder:text-sm placeholder:text-black py-4 px-3 ${
                        getFieldError(`defendant_${i}_address`)
                          ? "border-red-500"
                          : "border-[#CACACA]"
                      }`}
                      placeholder="Address"
                      onChange={(e) => handleDefendantInputChange(e, i)}
                      value={defendant.address}
                    />
                    {/* {getFieldError(`defendant_${i}_address`) && (
                      <ErrorMessage
                        message={getFieldError(`defendant_${i}_address`)!}
                      />
                    )} */}
                  </div>
                  <div className="mb-5 w-full">
                    <input
                      type="tel"
                      name="phone_number"
                      className={`w-full border rounded outline-none bg-transparent placeholder:text-sm placeholder:text-black py-4 px-3 ${
                        getFieldError(`defendant_${i}_phone_number`)
                          ? "border-red-500"
                          : "border-[#CACACA]"
                      }`}
                      placeholder="Phone number (e.g., 08012345678)"
                      onChange={(e) => handleDefendantInputChange(e, i)}
                      value={defendant.phone_number}
                    />
                    {/* {getFieldError(`defendant_${i}_phone_number`) && (
                      <ErrorMessage
                        message={getFieldError(`defendant_${i}_phone_number`)!}
                      />
                    )} */}
                  </div>

                  <div className="flex w-full gap-x-5 mb-5">
                    <div className="w-full">
                      <select
                        name="state"
                        className={`w-full border rounded outline-none bg-transparent py-4 px-3 ${
                          getFieldError(`defendant_${i}_state`)
                            ? "border-red-500"
                            : "border-[#CACACA]"
                        }`}
                        onChange={(e) => handleDefendantInputChange(e, i)}
                        value={defendant.state}
                      >
                        <option value="">Select State</option>
                        {NIGERIA_STATES.map((state: string) => (
                          <option key={state} value={state}>
                            {state}
                          </option>
                        ))}
                      </select>
                      {/* {getFieldError(`defendant_${i}_state`) && (
                        <ErrorMessage
                          message={getFieldError(`defendant_${i}_state`)!}
                        />
                      )} */}
                    </div>
                    <div className="w-full">
                      <select
                        name="lga"
                        className={`w-full border rounded outline-none bg-transparent py-4 px-3 ${
                          getFieldError(`defendant_${i}_lga`)
                            ? "border-red-500"
                            : "border-[#CACACA]"
                        }`}
                        onChange={(e) => handleDefendantInputChange(e, i)}
                        value={defendant.lga}
                        disabled={!defendant.state}
                      >
                        <option value="">Select LGA</option>
                        {defendant.state &&
                          (STATE_LGAS as Record<string, string[]>)[
                            defendant.state
                          ]?.map((lga) => (
                            <option key={lga} value={lga}>
                              {lga}
                            </option>
                          ))}
                      </select>
                      {/* {getFieldError(`defendant_${i}_lga`) && (
                        <ErrorMessage
                          message={getFieldError(`defendant_${i}_lga`)!}
                        />
                      )} */}
                    </div>
                  </div>

                  {/* Children Names Section */}

                  <div className="mb-5 w-full">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold">
                        Children Names{" "}
                        {/* <span className="text-red-500 font-bold">*</span>  */}
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          handleUpdateDefendantArray(i, "children_names", [
                            ...defendant.children_names,
                            "",
                          ])
                        }
                        className="text-xs text-blue-600 flex items-center gap-1"
                      >
                        <img src={BLUE_ADD} className="w-4 h-4" alt="" />
                        Add Child
                      </button>
                    </div>
                    {defendant.children_names.map((child, childIdx) => (
                      <div key={childIdx} className="flex gap-2 mb-2">
                        <div className="flex-1 flex flex-col">
                          <input
                            type="text"
                            placeholder="Child name"
                            value={child}
                            onChange={(e) => {
                              const newChildren = [...defendant.children_names];
                              newChildren[childIdx] = e.target.value;
                              handleUpdateDefendantArray(
                                i,
                                "children_names",
                                newChildren,
                              );
                            }}
                            className={`w-full border rounded py-2 px-3 text-sm ${
                              getFieldError(`defendant_${i}_child_${childIdx}`)
                                ? "border-red-500"
                                : "border-[#CACACA]"
                            }`}
                          />
                          {getFieldError(
                            `defendant_${i}_child_${childIdx}`,
                          ) && (
                            <ErrorMessage
                              message={
                                getFieldError(
                                  `defendant_${i}_child_${childIdx}`,
                                )!
                              }
                            />
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const newChildren = defendant.children_names.filter(
                              (_, idx) => idx !== childIdx,
                            );
                            handleUpdateDefendantArray(
                              i,
                              "children_names",
                              newChildren,
                            );
                          }}
                          className="text-red-500"
                        >
                          <FaMinusCircle size={18} />
                        </button>
                      </div>
                    ))}
                    {getFieldError(`defendant_${i}_children_names`) && (
                      <ErrorMessage
                        message={
                          getFieldError(`defendant_${i}_children_names`)!
                        }
                      />
                    )}
                  </div>

                  {/* Next of Kin Section */}
                  <div className="mb-5 w-full">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold">Next of Kin</span>
                      <button
                        type="button"
                        onClick={() =>
                          handleUpdateDefendantArray(i, "next_of_kin", [
                            ...defendant.next_of_kin,
                            { name: "", email: "", phone_number: "" },
                          ])
                        }
                        className="text-xs text-blue-600 flex items-center gap-1"
                      >
                        <img src={BLUE_ADD} className="w-4 h-4" alt="" />
                        Add Next of Kin
                      </button>
                    </div>
                    {defendant.next_of_kin.map((nok, nokIdx) => (
                      <div
                        key={nokIdx}
                        className="border p-3 rounded mb-2 border-[#CACACA] relative"
                      >
                        <button
                          type="button"
                          onClick={() => {
                            const newNok = defendant.next_of_kin.filter(
                              (_, idx) => idx !== nokIdx,
                            );
                            handleUpdateDefendantArray(
                              i,
                              "next_of_kin",
                              newNok,
                            );
                          }}
                          className="absolute top-2 right-2 text-red-500"
                        >
                          <FaMinusCircle size={18} />
                        </button>
                        <div className="flex flex-col gap-2">
                          <input
                            type="text"
                            placeholder="Name"
                            value={nok.name}
                            onChange={(e) => {
                              const newNok = [...defendant.next_of_kin];
                              newNok[nokIdx] = { ...nok, name: e.target.value };
                              handleUpdateDefendantArray(
                                i,
                                "next_of_kin",
                                newNok,
                              );
                            }}
                            className="w-full border rounded py-2 px-3 text-sm border-[#CACACA]"
                          />
                          <input
                            type="email"
                            placeholder="Email (optional)"
                            value={nok.email}
                            onChange={(e) => {
                              const newNok = [...defendant.next_of_kin];
                              newNok[nokIdx] = {
                                ...nok,
                                email: e.target.value,
                              };
                              handleUpdateDefendantArray(
                                i,
                                "next_of_kin",
                                newNok,
                              );
                            }}
                            className="w-full border rounded py-2 px-3 text-sm border-[#CACACA]"
                          />
                          <input
                            type="tel"
                            placeholder="Phone Number"
                            value={nok.phone_number}
                            onChange={(e) => {
                              const newNok = [...defendant.next_of_kin];
                              newNok[nokIdx] = {
                                ...nok,
                                phone_number: e.target.value,
                              };
                              handleUpdateDefendantArray(
                                i,
                                "next_of_kin",
                                newNok,
                              );
                            }}
                            className="w-full border rounded py-2 px-3 text-sm border-[#CACACA]"
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="flex items-center justify-between w-full">
                    {defendantField.length > 1 && (
                      <button
                        className=" flex items-center gap-x-2 text-sm rounded py-3  text-[#272755] font-semibold"
                        onClick={() => handleDeleteDefendant(i)}
                      >
                        <span className="font-medium text-sm text-red-500">
                          Delete Field
                        </span>
                        <FaMinusCircle size={20} color="red" />
                      </button>
                    )}
                    <button
                      className={`flex items-center ${
                        defendantField.length <= 1 &&
                        "w-full items-end justify-end"
                      } gap-x-2 text-sm rounded py-3  text-[#272755] font-semibold`}
                      onClick={handleAddDefendantField}
                    >
                      <span className="font-medium text-sm text-black">
                        Add Defendant
                      </span>
                      <img
                        src={BLUE_ADD}
                        className="w-[20px] h-[20px]"
                        alt=""
                      />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <button
              disabled={!stepValidation[1]}
              className={` ${
                stepValidation[1] ? "bg-opacity-100" : "bg-opacity-50"
              } w-full bg-[#272755] text-[17.8px] rounded py-3 mt-9 text-white font-semibold`}
              onClick={handleNext}
            >
              Next
            </button>
          </div>
        )}

        {currentStep === 2 && (
          <div className="w-[575px] h-full shadow-lg bg-white px-16 py-10 mt-6">
            <span className="text-[20px] font-semibold text-black">
              Details of Counsel
            </span>

            <div className="mt-6 w-full">
              <span className="text-[16px] font-semibold">
                Plaintiff Counsel
              </span>

              <form
                action=""
                className="flex items-start flex-col gap-y-5 py-3 w-full"
              >
                <div className="w-full">
                  <input
                    type="text"
                    name="counsel_detail_first_name"
                    className={`w-full border rounded outline-none bg-transparent placeholder:text-sm placeholder:text-black py-4 px-3 ${
                      getFieldError("counsel_detail_first_name")
                        ? "border-red-500"
                        : "border-[#CACACA]"
                    }`}
                    placeholder="First name"
                    onChange={handleInputChange}
                    value={formdata.counsel_detail_first_name}
                  />
                  {/* {getFieldError("counsel_detail_first_name") && (
                    <ErrorMessage
                      message={getFieldError("counsel_detail_first_name")!}
                    />
                  )} */}
                </div>
                <div className="w-full">
                  <input
                    type="text"
                    name="counsel_detail_last_name"
                    className={`w-full border rounded outline-none bg-transparent placeholder:text-sm placeholder:text-black py-4 px-3 ${
                      getFieldError("counsel_detail_last_name")
                        ? "border-red-500"
                        : "border-[#CACACA]"
                    }`}
                    placeholder="Last name"
                    onChange={handleInputChange}
                    value={formdata.counsel_detail_last_name}
                  />
                  {/* {getFieldError("counsel_detail_last_name") && (
                    <ErrorMessage
                      message={getFieldError("counsel_detail_last_name")!}
                    />
                  )} */}
                </div>
                <div className="w-full">
                  <input
                    type="text"
                    name="counsel_detail_firm_name"
                    className={`w-full border rounded outline-none bg-transparent placeholder:text-sm placeholder:text-black py-4 px-3 ${
                      getFieldError("counsel_detail_firm_name")
                        ? "border-red-500"
                        : "border-[#CACACA]"
                    }`}
                    placeholder="Name of law firm"
                    onChange={handleInputChange}
                    value={formdata.counsel_detail_firm_name}
                  />
                  {/* {getFieldError("counsel_detail_firm_name") && (
                    <ErrorMessage
                      message={getFieldError("counsel_detail_firm_name")!}
                    />
                  )} */}
                </div>
                <div className="w-full">
                  <input
                    type="email"
                    name="counsel_detail_email"
                    className={`w-full border rounded outline-none bg-transparent placeholder:text-sm placeholder:text-black py-4 px-3 ${
                      getFieldError("counsel_detail_email") &&
                      formdata.counsel_detail_email
                        ? "border-red-500"
                        : "border-[#CACACA]"
                    }`}
                    placeholder="Email address (optional)"
                    onChange={handleInputChange}
                    value={formdata.counsel_detail_email}
                  />
                  {/* {getFieldError("counsel_detail_email") && (
                    <ErrorMessage
                      message={getFieldError("counsel_detail_email")!}
                    />
                  )} */}
                </div>
                <div className="w-full">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold">
                      Address for service{" "}
                      <span className="text-red-500 font-bold">*</span>
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          counsel_detail_address: [
                            ...prev.counsel_detail_address,
                            "",
                          ],
                        }))
                      }
                      className="text-xs text-blue-600 flex items-center gap-1"
                    >
                      <img src={BLUE_ADD} className="w-4 h-4" alt="" />
                      Add Address
                    </button>
                  </div>
                  {formdata.counsel_detail_address.map((address, idx) => (
                    <div key={idx} className="flex gap-2 mb-2">
                      <div className="flex-1 flex flex-col">
                        <input
                          type="text"
                          placeholder="Address for service"
                          value={address}
                          onChange={(e) => {
                            const newAddresses = [
                              ...formdata.counsel_detail_address,
                            ];
                            newAddresses[idx] = sanitizeInput(e.target.value);
                            setFormData((prev) => ({
                              ...prev,
                              counsel_detail_address: newAddresses,
                            }));
                          }}
                          className={`w-full border rounded py-4 px-3 text-sm ${
                            getFieldError(`counsel_detail_address_${idx}`)
                              ? "border-red-500"
                              : "border-[#CACACA]"
                          }`}
                        />
                        {/* {getFieldError(`counsel_detail_address_${idx}`) && (
                          <ErrorMessage
                            message={
                              getFieldError(`counsel_detail_address_${idx}`)!
                            }
                          />
                        )} */}
                      </div>
                      {formdata.counsel_detail_address.length > 1 && (
                        <button
                          type="button"
                          onClick={() => {
                            const newAddresses =
                              formdata.counsel_detail_address.filter(
                                (_, i) => i !== idx,
                              );
                            setFormData((prev) => ({
                              ...prev,
                              counsel_detail_address: newAddresses,
                            }));
                          }}
                          className="text-red-500"
                        >
                          <FaMinusCircle size={18} />
                        </button>
                      )}
                    </div>
                  ))}
                  {/* {getFieldError("counsel_detail_address") && (
                    <ErrorMessage
                      message={getFieldError("counsel_detail_address")!}
                    />
                  )} */}
                </div>
                <div className="w-full">
                  <input
                    type="tel"
                    name="counsel_detail_phone_number"
                    className={`w-full border rounded outline-none bg-transparent placeholder:text-sm placeholder:text-black py-4 px-3 ${
                      getFieldError("counsel_detail_phone_number") &&
                      formdata.counsel_detail_phone_number
                        ? "border-red-500"
                        : "border-[#CACACA]"
                    }`}
                    placeholder="Phone number (e.g., 08012345678) (optional)"
                    onChange={handleInputChange}
                    value={formdata.counsel_detail_phone_number}
                  />
                  {/* {getFieldError("counsel_detail_phone_number") && (
                    <ErrorMessage
                      message={getFieldError("counsel_detail_phone_number")!}
                    />
                  )} */}
                </div>
                <div className="w-full">
                  <input
                    type="tel"
                    name="counsel_detail_whatsapp_number"
                    className={`w-full border rounded outline-none bg-transparent placeholder:text-sm placeholder:text-black py-4 px-3 ${
                      getFieldError("counsel_detail_whatsapp_number") &&
                      formdata.counsel_detail_whatsapp_number
                        ? "border-red-500"
                        : "border-[#CACACA]"
                    }`}
                    placeholder="WhatsApp number (e.g., 08012345678) (optional)"
                    onChange={handleInputChange}
                    value={formdata.counsel_detail_whatsapp_number}
                  />
                  {/* {getFieldError("counsel_detail_whatsapp_number") && (
                    <ErrorMessage
                      message={getFieldError("counsel_detail_whatsapp_number")!}
                    />
                  )} */}
                </div>
              </form>
            </div>
            <button
              disabled={!stepValidation[2]}
              className={` ${
                stepValidation[2] ? "bg-opacity-100" : "bg-opacity-50"
              } w-full bg-[#272755] text-[17.8px] rounded py-3 mt-9 text-white font-semibold`}
              onClick={handleNext}
            >
              Next
            </button>
            <button
              className="flex items-center gap-x-3  text-[17.8px] rounded py-3 mt-9 text-#272755 font-semibold"
              onClick={handlePrev}
            >
              <FaArrowLeft color="#272755" size={20} />
              <span>Back</span>
            </button>
          </div>
        )}

        {currentStep === 3 && (
          <div className="w-[575px] h-full shadow-lg bg-white p-10 mt-6">
            <span className="text-[20px] font-semibold text-black">
              Upload case documents
            </span>
            {caseDocument.map((docs, index) => (
              <>
                <div className="mt-2 flex-col flex gap-y-2" key={index}>
                  <span className="text-[16px] font-semibold">Title</span>
                  <div className="w-full">
                    <input
                      type="text"
                      name="title"
                      className={`w-full border rounded outline-none bg-transparent placeholder:text-sm placeholder:text-black py-4 px-3 ${
                        getFieldError(`document_${index}_title`)
                          ? "border-red-500"
                          : "border-[#CACACA]"
                      }`}
                      placeholder="Enter case title (motion, affidavit, address, exhibits. Etc)"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleCaseInputChange(e, index)
                      }
                      value={docs.title}
                    />
                    {getFieldError(`document_${index}_title`) && (
                      <ErrorMessage
                        message={getFieldError(`document_${index}_title`)!}
                      />
                    )}
                  </div>
                </div>

                {/* <div className="mt-4 flex-col flex gap-y-2">
                  <span className="text-[16px] font-semibold">
                    Document Type
                  </span>
                  <div className="w-full">
                    <select
                      name="document_type"
                      className={`w-full border rounded outline-none bg-transparent py-4 px-3 ${
                        getFieldError(`document_${index}_document_type`)
                          ? "border-red-500"
                          : "border-[#CACACA]"
                      }`}
                      onChange={(e) => handleCaseInputChange(e, index)}
                      value={docs.document_type}
                    >
                      <option value="">Select Document Type</option>
                      <option value="affidavit">Affidavit</option>
                      <option value="counter">Counter Claim</option>
                      <option value="motion">Motion</option>
                    </select>
                    {getFieldError(`document_${index}_document_type`) && (
                      <ErrorMessage
                        message={
                          getFieldError(`document_${index}_document_type`)!
                        }
                      />
                    )}
                  </div>
                </div> */}
                <div className="mt-2 flex-col flex gap-y-2" key={index}>
                  <span className="text-[16px] font-semibold">
                    Document Type
                  </span>
                  <div className="w-full">
                    <input
                      type="text"
                      name={`document_type`}
                      className={`w-full border rounded outline-none bg-transparent placeholder:text-sm placeholder:text-black py-4 px-3 ${
                        getFieldError(`document_${index}_document_type`)
                          ? "border-red-500"
                          : "border-[#CACACA]"
                      }`}
                      placeholder="Enter document type"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleCaseInputChange(e, index)
                      }
                      value={docs.document_type}
                    />
                    {getFieldError(`document_${index}_document_type`) && (
                      <ErrorMessage
                        message={
                          getFieldError(`document_${index}_document_type`)!
                        }
                      />
                    )}
                  </div>
                </div>

                <div className="mt-6 flex flex-col gap-y-2 items-start justify-start w-full">
                  <div className="flex flex-col gap-y-1 w-full">
                    <span className="text-[16px] font-semibold">
                      Upload documents
                    </span>
                    <button
                      onClick={() => setShowDocumentTypeModal(true)}
                      className="text-sm font-normal text-[#008FBD] w-fit"
                      type="button"
                    >
                      Documents to upload
                    </button>
                  </div>
                  {/* {getFieldError(`document_${index}_file`) && (
                    <ErrorMessage
                      message={getFieldError(`document_${index}_file`)!}
                    />
                  )} */}
                  {docs.file ? (
                    <div className="w-full h-full border border-[#C7C7CC] p-10 rounded-lg">
                      <div className="flex items-start gap-x-3">
                        <img src={PDF} className="w-[28px] h-[28px]" alt="" />
                        <div>
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-x-3">
                              <div className="flex flex-col gap-y-1">
                                <span className="text-sm font-medium text-[#232A35]">
                                  {docs.file.name}
                                </span>
                                <span className="text-[#667085] font-normal text-sm">
                                  PDF (max 25 MB)
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center h-[16px] w-[16px] justify-center rounded-full bg-[#272755] ">
                              <FaCheck size={10} color="white" />
                            </div>
                          </div>
                          <div className="flex items-center gap-x-3">
                            <div className="w-[268px] h-[8px] bg-[#272755] rounded-lg"></div>
                            <span>100 %</span>
                          </div>
                          <div className="flex items-center gap-x-2">
                            <button
                              type="button"
                              className="text-sm font-semibold text-[#6C757D]"
                              onClick={() => handleViewDocument(docs.file)}
                            >
                              View
                            </button>
                            <button
                              className="text-sm font-semibold text-[#6C757D]"
                              onClick={() => handleRemoveFileCaseField(index)}
                            >
                              Delete
                            </button>
                            <button
                              className="text-sm font-semibold text-[#6C757D]"
                              onClick={() =>
                                // @ts-ignore
                                // document
                                //   .querySelector(".file_upload")
                                //   [index].click()
                                fileInputRef.current[index].click()
                              }
                            >
                              Upload another
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div
                      className="w-full h-[126px] cursor-pointer  mt-3 border border-[#EAECF0] flex items-center justify-center flex-col rounded-md"
                      onClick={() =>
                        // @ts-ignore
                        fileInputRef.current[index].click()
                      }
                    >
                      <img
                        src={UPLOAD_ICON}
                        className="w-[40px] h-[40px]"
                        alt=""
                      />

                      <span className="text-[14px] font-semibold">
                        Click to upload{" "}
                        <span className="text-[#667085]">
                          Example receipt.pdf
                        </span>
                      </span>

                      <span className="text-[#667085] text-sm">
                        PDF (max 25mb)
                      </span>
                    </div>
                  )}

                  <input
                    type="file"
                    accept=".pdf"
                    className="file_upload sr-only"
                    ref={(ref: any) => (fileInputRef.current[index] = ref)}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleFileChange(e, index)
                    }
                  />
                  <div className="flex items-center justify-between w-full">
                    {caseDocument.length > 1 && (
                      <button
                        className=" flex items-center gap-x-2 text-sm rounded py-3  text-[#272755] font-semibold"
                        onClick={() => handleRemoveCaseField(index)}
                      >
                        <span className="font-medium text-sm text-red-500">
                          Delete Field
                        </span>
                        <FaMinusCircle size={20} color="red" />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={handleAddCaseField}
                      className={` ${
                        caseDocument.length <= 1 &&
                        "w-full flex justify-end items-end"
                      } `}
                    >
                      <img
                        src={ADD_BUTTON}
                        className="w-[20px] h-[20px] "
                        alt=""
                      />
                    </button>
                  </div>
                </div>
              </>
            ))}
            <div className="w-full flex items-start flex-col">
              <button
                disabled={!stepValidation[3] || loading}
                className={`w-full ${
                  stepValidation[3] && !loading
                    ? "bg-opacity-100"
                    : "bg-opacity-50 cursor-wait"
                } bg-[#272755] text-[17.8px] rounded py-3 mt-9 text-white font-semibold`}
                onClick={handleNext}
              >
                Next
              </button>
              {/* <button
                onClick={() => setShowDocumentTypeModal(true)}
                className="text-sm text-center w-full  mt-3 font-normal text-[#008FBD]"
              >
                Types of document to upload
              </button> */}
              <button
                className={`flex items-center gap-x-3  text-[17.8px] rounded py-3 mt-9 text-[#272755] font-semibold`}
                onClick={handlePrev}
              >
                <FaArrowLeft size={20} color="#272755" />
                <span>Back</span>
              </button>
            </div>
          </div>
        )}
        {currentStep === 4 && (
          <div className="w-[575px] h-full shadow-lg bg-white p-10 mt-6 flex flex-col gap-y-4">
            <span className="text-[20px] font-semibold text-black">
              Preview information
            </span>

            <div>
              <span className="text-[20px] font-semibold text-black">
                Case category
              </span>
              <div className="mt-3">
                <PreviewBox title="Selected Category" value={caseCategory} />
              </div>
            </div>

            <div>
              <span className="text-[20px] font-semibold text-black">
                Details of parties
              </span>
              <div className="flex items-start flex-col gap-y-2 mt-3">
                {plaintiffField.map((plaintiff) => (
                  <div className="flex items-start w-full flex-col gap-y-2 mt-3">
                    <span className="text-[16px] font-semibold text-black">
                      {plaintiff.partyType}
                    </span>
                    <PreviewBox
                      title="First name"
                      value={plaintiff.first_name}
                    />
                    <PreviewBox title="Last name" value={plaintiff.last_name} />
                    <PreviewBox
                      title="Other name"
                      value={plaintiff.other_name}
                    />
                    <PreviewBox title="Email address" value={plaintiff.email} />
                    <PreviewBox title="Address" value={plaintiff.address} />
                    <PreviewBox
                      title="Phone number"
                      value={plaintiff.phone_number}
                    />
                    <PreviewBox title="State" value={plaintiff.state} />
                    <PreviewBox title="LGA" value={plaintiff.lga} />
                    {plaintiff.children_names.length > 0 && (
                      <div className="w-full flex flex-col gap-y-1">
                        <span className="text-sm font-normal text-[#9A9A9A]">
                          Children Names
                        </span>
                        <div className="flex flex-wrap gap-2">
                          {plaintiff.children_names.map((name, idx) => (
                            <span
                              key={idx}
                              className="bg-gray-100 px-3 py-1 rounded text-sm"
                            >
                              {name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {plaintiff.next_of_kin.length > 0 && (
                      <div className="w-full flex flex-col gap-y-1">
                        <span className="text-sm font-normal text-[#9A9A9A]">
                          Next of Kin
                        </span>
                        {plaintiff.next_of_kin.map((nok, idx) => (
                          <div
                            key={idx}
                            className="border border-[#CACACA] rounded p-2 text-sm mb-1"
                          >
                            <div>
                              <strong>Name:</strong> {nok.name}
                            </div>
                            <div>
                              <strong>Email:</strong> {nok.email}
                            </div>
                            <div>
                              <strong>Phone:</strong> {nok.phone_number}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="flex items-start flex-col gap-y-2 mt-3">
                {defendantField.map((defendant) => (
                  <div className="flex items-start w-full flex-col gap-y-2 mt-3">
                    <span className="text-[16px] font-semibold text-black">
                      {defendant.partyType}
                    </span>
                    <PreviewBox
                      title="First name"
                      value={defendant.first_name}
                    />
                    <PreviewBox title="Last name" value={defendant.last_name} />
                    <PreviewBox
                      title="Other name"
                      value={defendant.other_name}
                    />
                    <PreviewBox title="Email address" value={defendant.email} />
                    <PreviewBox title="Address" value={defendant.address} />
                    <PreviewBox
                      title="Phone number"
                      value={defendant.phone_number}
                    />
                    <PreviewBox title="State" value={defendant.state} />
                    <PreviewBox title="LGA" value={defendant.lga} />
                    {defendant.children_names.length > 0 && (
                      <div className="w-full flex flex-col gap-y-1">
                        <span className="text-sm font-normal text-[#9A9A9A]">
                          Children Names
                        </span>
                        <div className="flex flex-wrap gap-2">
                          {defendant.children_names.map((name, idx) => (
                            <span
                              key={idx}
                              className="bg-gray-100 px-3 py-1 rounded text-sm"
                            >
                              {name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {defendant.next_of_kin.length > 0 && (
                      <div className="w-full flex flex-col gap-y-1">
                        <span className="text-sm font-normal text-[#9A9A9A]">
                          Next of Kin
                        </span>
                        {defendant.next_of_kin.map((nok, idx) => (
                          <div
                            key={idx}
                            className="border border-[#CACACA] rounded p-2 text-sm mb-1"
                          >
                            <div>
                              <strong>Name:</strong> {nok.name}
                            </div>
                            <div>
                              <strong>Email:</strong> {nok.email}
                            </div>
                            <div>
                              <strong>Phone:</strong> {nok.phone_number}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-4">
                <span className="text-[20px] font-semibold text-black">
                  Details of counsel
                </span>

                <div className="flex items-start flex-col gap-y-2 mt-3">
                  <span className="text-[16px] font-semibold text-black">
                    Claimant counsel
                  </span>
                  <PreviewBox
                    title="First name"
                    value={formdata.counsel_detail_first_name}
                  />
                  <PreviewBox
                    title="Last name"
                    value={formdata.counsel_detail_last_name}
                  />
                  <PreviewBox
                    title="Email address"
                    value={formdata.counsel_detail_email}
                  />
                  <div className="w-full h-full flex flex-col gap-y-1">
                    <span className="text-sm font-normal text-[#9A9A9A]">
                      Addresses for service
                    </span>
                    <div className="flex flex-col gap-1">
                      {formdata.counsel_detail_address.map((address, idx) => (
                        <div
                          key={idx}
                          className="border border-[#CACACA] rounded p-2 text-sm"
                        >
                          {address}
                        </div>
                      ))}
                    </div>
                  </div>
                  <PreviewBox
                    title="Phone number"
                    value={formdata.counsel_detail_phone_number}
                  />
                  <PreviewBox
                    title="Whatsapp number"
                    value={formdata.counsel_detail_whatsapp_number}
                  />
                  <PreviewBox
                    title="Name of firm"
                    value={formdata.counsel_detail_firm_name}
                  />
                </div>
              </div>

              <div className="mt-4">
                <span className="text-[20px] font-semibold text-black">
                  Uploaded documents
                </span>
                <div className="flex flex-col gap-y-3">
                  {caseDocument.map(
                    (doc, index) =>
                      doc.file && (
                        <div className="flex flex-col item-start gap-y-3">
                          <span className="font-medium text-[16px] text-black">
                            {doc.title} - {doc.document_type}
                          </span>

                          <div className="w-full h-full border border-[#C7C7CC] p-10 rounded-lg">
                            <div className="flex items-start gap-x-3">
                              <img
                                src={PDF}
                                className="w-[28px] h-[28px]"
                                alt=""
                              />
                              <div>
                                <div className="flex items-start justify-between">
                                  <div className="flex items-start gap-x-3">
                                    <div className="flex flex-col gap-y-1">
                                      <span className="text-sm font-medium text-[#232A35]">
                                        {doc.file.name}
                                      </span>
                                      <span className="text-[#667085] font-normal text-sm">
                                        PDF (max .25 MB)
                                      </span>
                                    </div>
                                  </div>
                                  <div className="flex items-center h-[16px] w-[16px] justify-center rounded-full bg-[#272755] ">
                                    <FaCheck size={10} color="white" />
                                  </div>
                                </div>
                                <div className="flex items-center gap-x-3">
                                  <div className="w-[268px] h-[8px] bg-[#272755] rounded-lg"></div>
                                  <span>100 %</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ),
                  )}
                </div>
              </div>
            </div>
            <button
              disabled={loading}
              className={`w-full ${
                !loading ? "bg-opacity-100" : "bg-opacity-50 cursor-wait"
              } bg-[#272755] text-[17.8px] rounded py-3 mt-9 text-white font-semibold`}
              onClick={handleCreateCase}
            >
              {loading ? "Please wait..." : "Submit"}
            </button>
            <button
              className="flex items-center gap-x-3  text-[17.8px] rounded py-3 mt-9 text-#272755 font-semibold"
              onClick={handlePrev}
            >
              <FaArrowLeft color="#272755" size={20} />
              <span>Back</span>
            </button>
          </div>
        )}

        <Modal
          open={successModal}
          close={() => setSuccessModal(false)}
          maxWidth={"xs"}
        >
          <div className="flex items-center justify-center flex-col px-5 py-5">
            <img src={SUCCESS_IMAGE} className="w-[211px] h-[171px]" alt="" />
            <span className="text-[20px] font-semibold">
              Submitted successfully
            </span>

            <button
              className={`w-full bg-[#272755] text-[17.8px] rounded py-3 mt-9 text-white font-semibold`}
              onClick={() => {
                setSuccessModal(false);
                navigate("/lawyer/my-files");
              }}
            >
              Close
            </button>
          </div>
        </Modal>
      </div>
      <Preloader
        preloaderState={preloaderState}
        action={"Creating a case folder"}
      />
      <Modal
        open={showDocumentTypeModal}
        close={() => setShowDocumentTypeModal(false)}
        maxWidth="sm"
      >
        <div className="px-5 w-full">
          <div className="flex flex-col gap-y-2">
            <span className="text-black font-bold text-2xl">Document Type</span>
            <p className="text-[#1D2739B2] font-normal text-sm">
              You are required to download, fill out these documents and upload
              them to complete this application.{" "}
            </p>
          </div>
          {filterEdDocumentsToUpload.length > 0 ? (
            <div className="flex items-start flex-col gap-3 mt-5 ">
              {filterEdDocumentsToUpload.map((item_) => (
                <div className="w-full border p-3 flex items-start gap-3 border-[#0000000D] rounded-lg">
                  <span className="text-[#1C1D21] font-bold text-sm">
                    {item_.name}
                  </span>{" "}
                  -{" "}
                  <a
                    href={item_.path}
                    target="_blank"
                    className="text-[#1C1D21] font-normal text-sm"
                  >
                    View and Download
                  </a>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center mt-5">
              <span className="text-center">
                The selected case category doesn't require a <br /> specific
                document to upload
              </span>
            </div>
          )}

          <button className="w-full bg-[#2B3674] text-xs rounded py-1.5  mt-9 mb-5 text-white font-normal">
            Close
          </button>
        </div>
      </Modal>
    </main>
  );
}

export default CreateCase;

export const PreviewBox = ({
  title,
  value,
}: {
  title: string;
  value: string;
}) => {
  return (
    <div className="w-full flex flex-col gap-y-1 items-start">
      <span className="text-sm font-normal text-[#9A9A9A]">{title}</span>
      <input
        type="text"
        name="counsel_detail_whatsapp_number"
        className="w-full border border-[#CACACA] rounded outline-none bg-transparent placeholder:text-sm placeholder:text-black py-4 px-3"
        placeholder=""
        // onChange={handleInputChange}
        value={value}
      />
    </div>
  );
};
